/**
 * What a potion is for.
 *
 * This is also the belt's grouping key — D2 fills the column that already holds
 * that kind — so it plays the part `group` plays for an affix: the axis along
 * which two of these are "the same thing" (`TODO.md` #49).
 */
export const POTION_KINDS = ['healing', 'mana', 'rejuvenation'];
const NOTHING = { flat: 0, percent: 0 };
/**
 * What one half of a potion puts back into a pool of this size, in whole
 * points.
 *
 * The two halves of a `PotionRestore` are additive on purpose rather than
 * exclusive: a healing potion is flat and nothing else, a rejuvenation is a
 * percentage of both pools, and a potion that were ever both would need no new
 * shape. Rounded down, like every other integer payout in core.
 *
 * It lives here, beside the type it reads, so `effects.ts`'s `heal` and
 * `mana.ts`'s `restoreMana` take the identical number from the identical rule
 * and neither module has to learn what a potion is. That is the half of
 * `TODO.md` #384 that was waiting on a mana pool to exist.
 */
export const restoreAmount = (restore, maximum) => {
    if (!(Number.isInteger(maximum) && maximum >= 1)) {
        throw new RangeError(`A pool is a positive whole number of points, received ${maximum}`);
    }
    return restore.flat + Math.floor((maximum * restore.percent) / 100);
};
const flat = (points) => ({ flat: points, percent: 0 });
const percent = (share) => ({ flat: 0, percent: share });
/** Every potion is one cell, which is what lets a belt column hold one per row. */
export const POTION_WIDTH = 1;
export const POTION_HEIGHT = 1;
export const POTION_BASES = [
    { id: 'minor-healing', name: 'Minor Healing Potion', kind: 'healing', tier: 1, qualityLevel: 1, cost: 30, life: flat(45), mana: NOTHING },
    { id: 'light-healing', name: 'Light Healing Potion', kind: 'healing', tier: 2, qualityLevel: 6, cost: 60, life: flat(90), mana: NOTHING },
    { id: 'healing', name: 'Healing Potion', kind: 'healing', tier: 3, qualityLevel: 12, cost: 110, life: flat(150), mana: NOTHING },
    { id: 'greater-healing', name: 'Greater Healing Potion', kind: 'healing', tier: 4, qualityLevel: 20, cost: 220, life: flat(270), mana: NOTHING },
    { id: 'super-healing', name: 'Super Healing Potion', kind: 'healing', tier: 5, qualityLevel: 32, cost: 440, life: flat(480), mana: NOTHING },
    { id: 'minor-mana', name: 'Minor Mana Potion', kind: 'mana', tier: 1, qualityLevel: 1, cost: 30, life: NOTHING, mana: flat(30) },
    { id: 'light-mana', name: 'Light Mana Potion', kind: 'mana', tier: 2, qualityLevel: 6, cost: 60, life: NOTHING, mana: flat(60) },
    { id: 'mana', name: 'Mana Potion', kind: 'mana', tier: 3, qualityLevel: 12, cost: 110, life: NOTHING, mana: flat(120) },
    { id: 'greater-mana', name: 'Greater Mana Potion', kind: 'mana', tier: 4, qualityLevel: 20, cost: 220, life: NOTHING, mana: flat(225) },
    { id: 'super-mana', name: 'Super Mana Potion', kind: 'mana', tier: 5, qualityLevel: 32, cost: 440, life: NOTHING, mana: flat(375) },
    { id: 'rejuvenation', name: 'Rejuvenation Potion', kind: 'rejuvenation', tier: 1, qualityLevel: 24, cost: 400, life: percent(35), mana: percent(35) },
    { id: 'full-rejuvenation', name: 'Full Rejuvenation Potion', kind: 'rejuvenation', tier: 2, qualityLevel: 40, cost: 1_200, life: percent(100), mana: percent(100) },
];
/** The potion with this id, or `undefined`. Absence is not an error — the caller decides. */
export const potionBaseById = (id, bases = POTION_BASES) => bases.find((entry) => entry.id === id);
/** Every potion that may drop at this item level. The gate is `<=`, as it is for gear. */
export const potionsAvailableAt = (itemLevel, bases = POTION_BASES) => bases.filter((entry) => entry.qualityLevel <= itemLevel);
/** Mints one potion from its type. */
export const createPotion = ({ id, base, itemLevel }) => {
    if (!Number.isInteger(itemLevel) || itemLevel < 1) {
        throw new RangeError(`Item level must be a positive integer, received ${itemLevel}`);
    }
    return {
        id,
        baseId: base.id,
        name: base.name,
        kind: base.kind,
        width: POTION_WIDTH,
        height: POTION_HEIGHT,
        itemLevel,
        value: base.cost,
        life: base.life,
        mana: base.mana,
    };
};
/**
 * Draws one potion type uniformly from what the item level allows, or
 * `undefined` when the table has nothing that deep.
 *
 * Uniform, like `rollAffixes` — a Minor Healing Potion is exactly as likely on
 * floor 40 as a Super one, which is not D2's behaviour and is the same known gap
 * `TODO.md` #35 records for affixes. Kept consistent with the existing roll
 * rather than fixed here in isolation.
 */
export const rollPotionBase = (itemLevel, rng, bases = POTION_BASES) => {
    const candidates = potionsAvailableAt(itemLevel, bases);
    return candidates.length === 0 ? undefined : candidates[rng.int(0, candidates.length)];
};
//# sourceMappingURL=potions.js.map